<template>
  <article class="v2-post-card v2-card v2-card--padded" :class="{ 'v2-post-card--pinned': isPinned }">
    <header class="v2-post-card__head">
      <div class="v2-post-card__author">
        <div class="v2-post-card__avatar" :aria-hidden="true">
          <img
            v-if="authorPicture"
            :src="authorPicture"
            :alt="`Logo de ${authorName}`"
            class="v2-post-card__avatar-img"
          />
          <span v-else>{{ authorInitials }}</span>
        </div>
        <div class="v2-post-card__author-meta">
          <p class="v2-post-card__author-name">{{ authorName }}</p>
          <p class="v2-post-card__author-date">
            <v-icon size="12">mdi-clock-outline</v-icon>
            <span :title="absoluteDate">{{ relativeDate }}</span>
          </p>
        </div>
      </div>

      <div class="v2-post-card__head-right">
        <span v-if="isPinned" class="v2-post-card__pin" aria-label="Comunicado fijado">
          <v-icon size="14">mdi-pin</v-icon>
          Fijado
        </span>
        <span class="v2-post-card__type">
          <v-icon size="12">{{ typeIcon }}</v-icon>
          {{ typeLabel }}
        </span>
      </div>
    </header>

    <h3 v-if="title" class="v2-post-card__title">{{ title }}</h3>

    <div v-if="type === 'BLOG' && hasContent" class="v2-post-card__body">
      <div class="v2-post-card__blog" v-html="renderedContent" />
    </div>

    <div v-else-if="type === 'VIDEO' && youtubeEmbedUrl" class="v2-post-card__video">
      <iframe
        :src="youtubeEmbedUrl"
        :title="title"
        frameborder="0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        allowfullscreen
      />
    </div>

    <div v-else-if="type === 'DOC' && documentUrl" class="v2-post-card__doc">
      <!--
        Patrón legacy (communityCard.vue):
          - PDF: <embed type="application/pdf">; si la URL es externa y
            no es blob, se envuelve en Google Docs Viewer para forzar
            render inline sin auto-descarga del navegador.
          - Imagen: <img> / <v-img> directo.
      -->
      <div class="v2-post-card__doc-preview">
        <embed
          v-if="documentKind === 'pdf'"
          width="100%"
          type="application/pdf"
          :height="documentPdfHeight"
          :src="documentEmbedSrc"
          class="v2-post-card__doc-frame"
        />
        <img
          v-else-if="documentKind === 'image'"
          :src="documentUrl"
          :alt="documentName"
          class="v2-post-card__doc-image"
          loading="lazy"
        />
        <div v-else class="v2-post-card__doc-placeholder">
          <span class="v2-post-card__doc-placeholder-icon">
            <v-icon size="40" color="primary">{{ documentIcon }}</v-icon>
          </span>
          <span class="v2-post-card__doc-placeholder-label">
            {{ documentKindLabel }}
          </span>
        </div>
      </div>

      <div class="v2-post-card__doc-meta">
        <p class="v2-post-card__doc-name">{{ documentName }}</p>
        <p class="v2-post-card__doc-hint">{{ documentHint }}</p>
      </div>

      <div class="v2-post-card__doc-actions">
        <a
          href="#"
          class="v2-post-card__doc-link"
          @click.prevent="openDocument"
        >
          <v-icon size="16">mdi-open-in-new</v-icon>
          {{ documentOpenLabel }}
        </a>
      </div>
    </div>

    <div v-else-if="type === 'LINK' && linkUrl" class="v2-post-card__link">
      <a :href="linkUrl" target="_blank" rel="noopener noreferrer">
        <v-icon size="16" color="primary">mdi-link-variant</v-icon>
        <span>{{ linkLabel }}</span>
        <v-icon size="14">mdi-open-in-new</v-icon>
      </a>
    </div>

    <div v-else-if="type === 'POLL'" class="v2-post-card__poll">
      <div v-if="!survey" class="v2-post-card__poll-status">
        <v-icon size="20" color="primary">mdi-poll</v-icon>
        <span>Esta encuesta no está habilitada para responder.</span>
      </div>
      <div v-else-if="surveyResponded" class="v2-post-card__poll-status">
        <v-icon size="20" color="success">mdi-check-circle-outline</v-icon>
        <span>Tu SOFOM ya respondió esta encuesta.</span>
      </div>
      <div v-else-if="survey.availability !== 'OPEN'" class="v2-post-card__poll-status">
        <v-icon size="20" color="primary">mdi-poll</v-icon>
        <span>{{ surveyAvailabilityLabel }}</span>
      </div>
      <form v-else class="v2-post-card__poll-form" @submit.prevent="submitSurvey">
        <fieldset
          v-for="(question, index) in surveyQuestions"
          :key="questionKey(question, index)"
          class="v2-post-card__poll-question"
        >
          <legend>{{ question.text }}</legend>
          <label
            v-for="option in question.options || []"
            :key="option"
            class="v2-post-card__poll-option"
          >
            <input
              type="radio"
              :name="`survey-${survey._id}-${questionKey(question, index)}`"
              :value="option"
              :checked="surveyAnswers[questionKey(question, index)] === option"
              @change="$set(surveyAnswers, questionKey(question, index), option)"
            />
            <span>{{ option }}</span>
          </label>
          <textarea
            v-if="!question.options || !question.options.length"
            :value="surveyAnswers[questionKey(question, index)] || ''"
            rows="2"
            :aria-label="question.text"
            placeholder="Escribe tu respuesta"
            @input="$set(surveyAnswers, questionKey(question, index), $event.target.value)"
          />
        </fieldset>
        <p v-if="surveyError" class="v2-post-card__poll-error" role="alert">{{ surveyError }}</p>
        <button class="v2-post-card__poll-submit" type="submit" :disabled="submittingSurvey">
          {{ submittingSurvey ? "Enviando…" : "Enviar respuestas" }}
        </button>
      </form>
    </div>

    <div
      v-if="images.length && type !== 'DOC'"
      class="v2-post-card__images"
      :class="`v2-post-card__images--${images.length}`"
    >
      <a
        v-for="(img, idx) in images"
        :key="img + idx"
        :href="img"
        target="_blank"
        rel="noopener noreferrer"
        class="v2-post-card__image"
      >
        <img :src="img" :alt="`Imagen ${idx + 1} de ${title}`" loading="lazy" />
      </a>
    </div>

    <p v-if="description" class="v2-post-card__description">{{ description }}</p>

    <ul v-if="labels.length" class="v2-post-card__labels" aria-label="Etiquetas">
      <li v-for="label in labels" :key="label" class="v2-post-card__label">#{{ label }}</li>
    </ul>
  </article>
</template>

<script>
import { mapActions } from "vuex";
import {
  isPostPinned,
  getPostTitle,
  getPostDescription,
  getPostContent,
  getPostType,
  getPostImages,
  getPostLabels,
  getPostDate,
  getPostAuthor,
  formatPostDate,
  formatPostRelative,
  getYouTubeEmbedUrl,
  getDocumentUrl,
  resolveDocumentKind,
  getDocumentName,
  getLinkUrl,
  resolvePostTypeLabel,
  resolvePostTypeIcon,
} from "./communityHelpers";

const stripHtml = (html) => (typeof html === "string" ? html.replace(/<[^>]+>/g, " ").replace(/\s+/g, " ").trim() : "");

const sanitizeContent = (html) => {
  if (typeof html !== "string") return "";
  return html
    .replace(/<script[\s\S]*?>[\s\S]*?<\/script>/gi, "")
    .replace(/\son\w+="[^"]*"/gi, "")
    .replace(/javascript:/gi, "");
};

const DEFAULT_ORG = {
  id: null,
  name: "Cámara WeChamber",
  picture: null,
  initials: "CW",
};

export default {
  name: "PostCard",
  props: {
    post: { type: Object, required: true },
    survey: { type: Object, default: null },
    organization: {
      type: Object,
      default: () => DEFAULT_ORG,
    },
  },
  data() {
    return {
      surveyAnswers: {},
      surveyError: "",
      submittingSurvey: false,
      submittedSurvey: false,
    };
  },
  computed: {
    surveyQuestions() {
      const questions = this.survey?.asocoinsSurvey?.questions;
      return Array.isArray(questions) ? questions : [];
    },
    surveyResponded() {
      return this.submittedSurvey || Boolean(this.survey && this.survey.responded);
    },
    surveyAvailabilityLabel() {
      if (this.survey && this.survey.availability === "NOT_STARTED") return "Esta encuesta aún no está abierta.";
      if (this.survey && this.survey.availability === "CLOSED") return "El periodo para responder esta encuesta terminó.";
      return "Esta encuesta no está disponible para responder.";
    },
    isPinned() {
      return isPostPinned(this.post);
    },
    title() {
      return getPostTitle(this.post);
    },
    description() {
      return getPostDescription(this.post);
    },
    type() {
      return getPostType(this.post);
    },
    typeLabel() {
      return resolvePostTypeLabel(this.type);
    },
    typeIcon() {
      return resolvePostTypeIcon(this.type);
    },
    images() {
      return getPostImages(this.post);
    },
    labels() {
      return getPostLabels(this.post);
    },
    hasContent() {
      return Boolean(getPostContent(this.post));
    },
    renderedContent() {
      return sanitizeContent(getPostContent(this.post));
    },
    youtubeEmbedUrl() {
      return getYouTubeEmbedUrl(this.post);
    },
    documentUrl() {
      return getDocumentUrl(this.post);
    },
    documentKind() {
      return resolveDocumentKind(this.post);
    },
    documentName() {
      return getDocumentName(this.post);
    },
    documentEmbedSrc() {
      const url = this.documentUrl;
      if (!url) return "";
      if (url.startsWith("blob:")) return url;
      return `https://drive.google.com/viewerng/viewer?embedded=true&url=${encodeURIComponent(url)}`;
    },
    documentPdfHeight() {
      if (typeof window === "undefined") return "650";
      return window.innerWidth < 768 ? "480" : "650";
    },
    documentKindLabel() {
      switch (this.documentKind) {
        case "image":
          return "Imagen";
        case "pdf":
          return "PDF";
        case "video":
          return "Video";
        case "audio":
          return "Audio";
        default:
          return "Adjunto";
      }
    },
    documentIcon() {
      switch (this.documentKind) {
        case "pdf":
          return "mdi-file-pdf-box";
        case "video":
          return "mdi-video-outline";
        case "audio":
          return "mdi-music-note-outline";
        default:
          return "mdi-file-document-outline";
      }
    },
    documentHint() {
      switch (this.documentKind) {
        case "image":
          return "Imagen adjunta · vista previa habilitada";
        default:
          return "Adjunto · ábrelo o descárgalo para verlo completo";
      }
    },
    documentOpenLabel() {
      switch (this.documentKind) {
        case "image":
          return "Ver imagen";
        case "pdf":
          return "Abrir PDF";
        case "video":
          return "Reproducir video";
        case "audio":
          return "Reproducir audio";
        default:
          return "Abrir";
      }
    },
    linkUrl() {
      return getLinkUrl(this.post);
    },
    linkLabel() {
      const url = this.linkUrl;
      if (!url) return "";
      try {
        const u = new URL(url);
        return u.hostname.replace(/^www\./, "") + (u.pathname !== "/" ? u.pathname : "");
      } catch (e) {
        return url;
      }
    },
    absoluteDate() {
      return formatPostDate(this.post);
    },
    relativeDate() {
      return formatPostRelative(this.post);
    },
    authorName() {
      const author = getPostAuthor(this.post, this.organization);
      return author ? author.name : "Cámara WeChamber";
    },
    authorPicture() {
      const author = getPostAuthor(this.post, this.organization);
      return author ? author.picture : null;
    },
    authorInitials() {
      const author = getPostAuthor(this.post, this.organization);
      return author ? author.initials : "CW";
    },
  },
  watch: {
    survey() {
      this.surveyAnswers = {};
      this.surveyError = "";
      this.submittedSurvey = false;
    },
  },
  methods: {
    ...mapActions("asocoins", ["respondSurvey"]),
    openDocument() {
      if (!this.documentUrl) return;
      window.open(this.documentUrl, "_blank", "noopener,noreferrer");
    },
    questionKey(question, index) {
      return String(question && (question.id || question._id) || `question-${index + 1}`);
    },
    async submitSurvey() {
      if (!this.survey || this.survey.availability !== "OPEN" || this.surveyResponded || this.submittingSurvey) return;
      this.surveyError = "";
      this.submittingSurvey = true;
      const answers = this.surveyQuestions.reduce((result, question, index) => {
        result[this.questionKey(question, index)] = String(this.surveyAnswers[this.questionKey(question, index)] || "").trim();
        return result;
      }, {});
      const response = await this.respondSurvey({ surveyId: this.survey._id, answers });
      this.submittingSurvey = false;
      if (!response.status) {
        this.surveyError = response.message || "No se pudo enviar la respuesta. Inténtalo de nuevo.";
        return;
      }
      this.submittedSurvey = true;
      this.$emit("survey-responded");
    },
  },
};
</script>

<style scoped>
.v2-post-card {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  position: relative;
  transition: transform var(--transition-base), box-shadow var(--transition-base);
}

.v2-post-card:hover {
  box-shadow: var(--shadow-card-hover);
}

.v2-post-card--pinned {
  border-color: var(--color-amber);
  border-width: 1px;
  box-shadow: 0 0 0 1px var(--color-amber-soft) inset;
}

.v2-post-card__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: var(--space-3);
  flex-wrap: wrap;
}

.v2-post-card__author {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  min-width: 0;
}

.v2-post-card__avatar {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  border-radius: var(--radius-full);
  background: var(--color-surface-low);
  border: 1px solid var(--color-border);
  color: var(--color-primary);
  font-family: var(--font-headline);
  font-size: var(--fs-label-md);
  font-weight: var(--fw-semibold);
  flex-shrink: 0;
  text-transform: uppercase;
  overflow: hidden;
}

.v2-post-card__avatar-img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: var(--color-surface);
  padding: var(--space-1);
}

.v2-post-card__author-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.v2-post-card__author-name {
  margin: 0;
  font-family: var(--font-body);
  font-size: var(--fs-body-md);
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-post-card__author-date {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
}

.v2-post-card__head-right {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  flex-wrap: wrap;
}

.v2-post-card__pin,
.v2-post-card__type {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-1) var(--space-3);
  font-size: var(--fs-label-xs);
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
  border-radius: var(--radius-full);
  line-height: 1;
  white-space: nowrap;
}

.v2-post-card__pin {
  background-color: var(--color-amber);
  color: var(--color-amber-on);
}

.v2-post-card__type {
  background-color: var(--color-surface-low);
  color: var(--color-text-muted);
}

.v2-post-card__title {
  margin: 0;
  font-family: var(--font-headline);
  font-size: var(--fs-display-md);
  font-weight: var(--fw-semibold);
  line-height: var(--lh-snug);
  color: var(--color-text);
}

.v2-post-card__body {
  display: flex;
  flex-direction: column;
}

.v2-post-card__blog {
  font-size: var(--fs-body-md);
  color: var(--color-text);
  line-height: 1.6;
  overflow-wrap: break-word;
}

.v2-post-card__blog :deep(p) {
  margin: 0 0 var(--space-2) 0;
}

.v2-post-card__blog :deep(p:last-child) {
  margin-bottom: 0;
}

.v2-post-card__blog :deep(img) {
  max-width: 100%;
  height: auto;
  border-radius: var(--radius-md);
  margin: var(--space-2) 0;
}

.v2-post-card__blog :deep(a) {
  color: var(--color-primary);
  text-decoration: underline;
  word-break: break-word;
}

.v2-post-card__blog :deep(blockquote) {
  border-left: 3px solid var(--color-amber);
  padding-left: var(--space-3);
  margin: var(--space-3) 0;
  color: var(--color-text-muted);
  font-style: italic;
}

.v2-post-card__blog :deep(strong) {
  font-weight: var(--fw-semibold);
}

.v2-post-card__blog :deep(ul),
.v2-post-card__blog :deep(ol) {
  margin: var(--space-2) 0;
  padding-left: var(--space-5);
}

.v2-post-card__blog :deep(li) {
  margin-bottom: var(--space-1);
}

.v2-post-card__video {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: var(--radius-md);
  overflow: hidden;
  background-color: var(--color-neutral-900);
}

.v2-post-card__video iframe {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  border: 0;
}

.v2-post-card__doc {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-3);
  background-color: var(--color-surface-low);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

.v2-post-card__doc-preview {
  position: relative;
  width: 100%;
  border-radius: var(--radius-md);
  overflow: hidden;
  background-color: var(--color-neutral-100);
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 160px;
  border: 1px solid var(--color-border);
}

.v2-post-card__doc-image {
  display: block;
  width: 100%;
  height: auto;
  max-height: 480px;
  object-fit: contain;
  background-color: var(--color-neutral-100);
}

.v2-post-card__doc-frame {
  display: block;
  width: 100%;
  border: 0;
  background-color: var(--color-neutral-900);
}

.v2-post-card__doc-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  padding: var(--space-6);
  text-align: center;
  color: var(--color-text-muted);
}

.v2-post-card__doc-placeholder-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 64px;
  height: 64px;
  border-radius: var(--radius-full);
  background-color: var(--color-primary-soft);
}

.v2-post-card__doc-placeholder-label {
  font-family: var(--font-body);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--color-text-muted);
  text-transform: uppercase;
  letter-spacing: var(--tracking-wider);
}

.v2-post-card__doc-meta {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.v2-post-card__doc-name {
  margin: 0;
  font-weight: var(--fw-semibold);
  color: var(--color-text);
  word-break: break-all;
  font-size: var(--fs-body-sm);
}

.v2-post-card__doc-hint {
  margin: 0;
  font-size: var(--fs-body-sm);
  color: var(--color-text-muted);
}

.v2-post-card__doc-actions {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
}

.v2-post-card__doc-link {
  display: inline-flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  background-color: var(--color-primary);
  color: var(--color-primary-on);
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  border-radius: var(--radius-md);
  text-decoration: none;
  flex-shrink: 0;
  transition: background-color var(--transition-fast);
}

.v2-post-card__doc-link:hover {
  background-color: var(--color-primary-hover);
  color: var(--color-primary-on);
}

.v2-post-card__doc-link--secondary {
  background-color: var(--color-surface);
  color: var(--color-primary);
  border: 1px solid var(--color-border-strong);
}

.v2-post-card__doc-link--secondary:hover {
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
}

.v2-post-card__link a {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  padding: var(--space-3);
  background-color: var(--color-surface-low);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  color: var(--color-text);
  text-decoration: none;
  font-weight: var(--fw-medium);
  width: 100%;
  overflow: hidden;
}

.v2-post-card__link a:hover {
  border-color: var(--color-primary);
  background-color: var(--color-primary-soft);
}

.v2-post-card__link a span {
  flex: 1 1 auto;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.v2-post-card__poll {
  display: block;
  padding: var(--space-3);
  background-color: var(--color-primary-soft);
  color: var(--color-primary);
  border-radius: var(--radius-md);
  font-size: var(--fs-body-sm);
}

.v2-post-card__poll-status {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}

.v2-post-card__poll-form {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.v2-post-card__poll-question {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
  min-width: 0;
  margin: 0;
  padding: 0;
  border: 0;
}

.v2-post-card__poll-question legend {
  margin-bottom: var(--space-2);
  color: var(--color-text);
  font-weight: var(--fw-semibold);
}

.v2-post-card__poll-option {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  color: var(--color-text);
  cursor: pointer;
}

.v2-post-card__poll-option input {
  accent-color: var(--color-primary);
}

.v2-post-card__poll-question textarea {
  width: 100%;
  resize: vertical;
  padding: var(--space-2);
  border: 1px solid var(--color-border-strong);
  border-radius: var(--radius-sm);
  color: var(--color-text);
  background: var(--color-surface);
}

.v2-post-card__poll-submit {
  align-self: flex-start;
  padding: var(--space-2) var(--space-4);
  border: 0;
  border-radius: var(--radius-md);
  color: #fff;
  background: var(--color-primary);
  font-weight: var(--fw-semibold);
  cursor: pointer;
}

.v2-post-card__poll-submit:disabled {
  opacity: 0.65;
  cursor: wait;
}

.v2-post-card__poll-error {
  margin: 0;
  color: var(--color-danger, #b42318);
}

.v2-post-card__images {
  display: grid;
  gap: var(--space-2);
}

.v2-post-card__images--1 {
  grid-template-columns: 1fr;
}

.v2-post-card__images--2 {
  grid-template-columns: repeat(2, 1fr);
}

.v2-post-card__images--3,
.v2-post-card__images--4 {
  grid-template-columns: repeat(2, 1fr);
}

.v2-post-card__images--5,
.v2-post-card__images--6 {
  grid-template-columns: repeat(3, 1fr);
}

.v2-post-card__image {
  display: block;
  border-radius: var(--radius-md);
  overflow: hidden;
  background-color: var(--color-neutral-100);
  aspect-ratio: 1;
}

.v2-post-card__image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform var(--transition-base);
}

.v2-post-card__image:hover img {
  transform: scale(1.02);
}

.v2-post-card__description {
  margin: 0;
  font-size: var(--fs-body-md);
  color: var(--color-text);
  line-height: 1.5;
}

.v2-post-card__labels {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  padding-top: var(--space-2);
  border-top: 1px solid var(--color-border);
}

.v2-post-card__label {
  font-size: var(--fs-body-sm);
  font-weight: var(--fw-medium);
  color: var(--color-primary);
}
</style>
